import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';

// Flag filtering — only active Flag entries for drones in the fleet.
// Fleet scoping (generation, model, archived) is handled by the backend,
// but /flags returns all flags, so we cross-reference against loaded drones.
const filterFlags = (flags, drones) => {
  const fleetSerials = new Set(drones.map(d => d.serialnumber));
  return flags.filter((flag) => flag.active && flag.statusdesc === 'Flag' && fleetSerials.has(flag.drone_serial));
};

const useFleetStore = create((set, get) => ({
  // State
  drones: [],
  flags: [],
  loading: false,
  error: null,
  _cancelled: false, // Internal flag to handle race condition on logout

  // Actions
  fetchFleet: async (onUnauthorized) => {
    set({ loading: true, error: null, _cancelled: false });
    try {
      const [drones, flags] = await Promise.all([
        apiFetchAuthed('/fleet', { onUnauthorized }),
        apiFetchAuthed('/flags', { onUnauthorized }),
      ]);

      set({
        drones,
        flags: filterFlags(flags, drones),
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: error.message });
      // Don't show alert if it's auth error - already handled by onUnauthorized
      if (error.status !== 401) {
        console.error('Error fetching fleet data:', error);
      }
    }
  },

  // Refresh fleet data in background (with deduplication via IIFE closure)
  refreshFleet: (() => {
    let refreshPromise = null;

    return async () => {
      // If already refreshing, return existing promise (deduplication)
      if (refreshPromise) {
        return refreshPromise;
      }

      refreshPromise = (async () => {
        try {
          const [drones, flags] = await Promise.all([
            apiFetchAuthed('/fleet', { silent: true }),
            apiFetchAuthed('/flags', { silent: true }),
          ]);

          // Check if store was cleared during fetch (race condition prevention)
          if (get()._cancelled) {
            if (process.env.NODE_ENV === 'development') {
              console.debug('[Fleet Store] Refresh cancelled - store was cleared');
            }
            return;
          }

          set({
            drones,
            flags: filterFlags(flags, drones),
          });
        } catch (error) {
          // Silent fail for users, but log for debugging in development
          if (process.env.NODE_ENV === 'development') {
            console.debug('[Fleet Store] Background refresh failed:', error.message);
          }
        } finally {
          refreshPromise = null;
        }
      })();

      return refreshPromise;
    };
  })(),

  // Update a single drone in the store (for optimistic updates)
  updateDrone: (serialnumber, updates) => {
    set((state) => ({
      drones: state.drones.map((drone) =>
        drone.serialnumber === serialnumber ? { ...drone, ...updates } : drone
      ),
    }));
  },

  // Add or update a flag
  updateFlag: (flag) => {
    set((state) => {
      const existingIndex = state.flags.findIndex(
        (f) => f.drone_serial === flag.drone_serial
      );
      if (existingIndex >= 0) {
        const newFlags = [...state.flags];
        newFlags[existingIndex] = flag;
        return { flags: newFlags };
      }
      return { flags: [...state.flags, flag] };
    });
  },

  // Remove a flag
  removeFlag: (droneSerial) => {
    set((state) => ({
      flags: state.flags.filter((f) => f.drone_serial !== droneSerial),
    }));
  },

  // Clear all data (for logout)
  clearFleet: () => {
    set({
      drones: [],
      flags: [],
      loading: false,
      error: null,
      _cancelled: true // Signal any in-flight refreshes to abort
    });
  },
}));

export default useFleetStore;
